import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useAuth } from './AuthContext';
import { useFamily } from './FamilyContext';
import {
  subscribeToAppointments,
  createAppointment as svcCreateAppointment,
  updateAppointment as svcUpdateAppointment,
  completeAppointment as svcCompleteAppointment,
  cancelAppointment as svcCancelAppointment,
  softDeleteAppointment as svcSoftDeleteAppointment,
} from '../services/appointmentService';

export const AppointmentsContext = createContext(null);

/**
 * Provider de citas del Calendario para el hogar activo.
 * Se monta dentro de AppShell, después de PetsProvider (necesita pets para
 * que la UI pueda resolver `petIds` → nombres).
 *
 * Estado:
 *  - appointments: array de citas activas (no soft-deleted), ordenadas asc
 *    por `scheduledAt`.
 *  - status: 'idle' | 'loading' | 'ready' | 'error'.
 *  - error: AppError | null.
 */
export const AppointmentsProvider = ({ children }) => {
  const { profile } = useAuth();
  const { family } = useFamily();
  const familyId = family?.id ?? null;

  const [appointments, setAppointments] = useState([]);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!familyId) {
      setAppointments([]);
      setStatus('idle');
      return;
    }

    setStatus('loading');
    const unsubscribe = subscribeToAppointments(
      familyId,
      (next) => {
        const sorted = [...next].sort((a, b) => {
          const aTime = a.scheduledAt?.toMillis?.() ?? 0;
          const bTime = b.scheduledAt?.toMillis?.() ?? 0;
          return aTime - bTime; // asc: las próximas primero
        });
        setAppointments(sorted);
        setStatus('ready');
        setError(null);
      },
      (err) => {
        setError(err);
        setStatus('error');
      }
    );

    return unsubscribe;
  }, [familyId]);

  // ===== Mutations =====

  const createAppointment = useCallback(
    async (input) => {
      if (!profile?.uid || !familyId) throw new Error('Sin sesión o sin hogar');
      return svcCreateAppointment({ ...input, uid: profile.uid, familyId });
    },
    [profile?.uid, familyId]
  );

  const updateAppointment = useCallback(
    async (appointmentId, updates) => {
      if (!profile?.uid) throw new Error('Sin sesión');
      return svcUpdateAppointment({ uid: profile.uid, appointmentId, updates });
    },
    [profile?.uid]
  );

  const completeAppointment = useCallback(
    async (appointmentId) => {
      if (!profile?.uid) throw new Error('Sin sesión');
      return svcCompleteAppointment({ uid: profile.uid, appointmentId });
    },
    [profile?.uid]
  );

  const cancelAppointment = useCallback(
    async (appointmentId) => {
      if (!profile?.uid) throw new Error('Sin sesión');
      return svcCancelAppointment({ uid: profile.uid, appointmentId });
    },
    [profile?.uid]
  );

  const softDeleteAppointment = useCallback(
    async (appointmentId) => {
      if (!profile?.uid) throw new Error('Sin sesión');
      return svcSoftDeleteAppointment({ uid: profile.uid, appointmentId });
    },
    [profile?.uid]
  );

  const getAppointmentById = useCallback(
    (appointmentId) => appointments.find((a) => a.id === appointmentId) ?? null,
    [appointments]
  );

  const value = useMemo(
    () => ({
      appointments,
      status,
      error,
      isLoading: status === 'loading',
      isEmpty: status === 'ready' && appointments.length === 0,
      getAppointmentById,
      createAppointment,
      updateAppointment,
      completeAppointment,
      cancelAppointment,
      softDeleteAppointment,
    }),
    [
      appointments,
      status,
      error,
      getAppointmentById,
      createAppointment,
      updateAppointment,
      completeAppointment,
      cancelAppointment,
      softDeleteAppointment,
    ]
  );

  return (
    <AppointmentsContext.Provider value={value}>
      {children}
    </AppointmentsContext.Provider>
  );
};

export const useAppointments = () => {
  const ctx = useContext(AppointmentsContext);
  if (!ctx) {
    throw new Error('useAppointments must be used within an AppointmentsProvider');
  }
  return ctx;
};
